import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { setToken } from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function extractSessionId() {
  // Try hash fragment: #session_id=xxx
  const hashMatch = window.location.hash.match(/session_id=([^&]+)/);
  if (hashMatch) return hashMatch[1];
  
  // Try query param: ?session_id=xxx
  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get("session_id");
  if (fromQuery) return fromQuery;
  
  // Try anywhere in the URL as fallback
  const urlMatch = window.location.href.match(/session_id=([^&#]+)/);
  if (urlMatch) return urlMatch[1];
  
  return null;
}

export const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState("Inloggen met Google...");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Debug: log the full URL to the backend
    axios.post(`${API}/auth/debug-callback`, {
      href: window.location.href,
      hash: window.location.hash,
      search: window.location.search,
    }).catch(() => {});

    const sessionId = extractSessionId();
    if (!sessionId) {
      setStatus("Geen sessie gevonden. Probeer opnieuw in te loggen.");
      toast.error("Google login mislukt: geen sessie ontvangen");
      setTimeout(() => navigate("/login", { replace: true }), 3000);
      return;
    }

    const exchangeSession = async (retries = 3) => {
      // Pick up any invite token that was persisted before the Google redirect
      const inviteToken = sessionStorage.getItem("sportly_invite_token") || null;
      for (let i = 0; i < retries; i++) {
        try {
          setStatus(`Sessie verifiëren... (poging ${i + 1})`);
          const res = await axios.post(`${API}/auth/session`, {
            session_id: sessionId,
            invite_token: inviteToken,
          });
          setToken(res.data.session_token);
          sessionStorage.removeItem("sportly_invite_token");
          // Full-page redirect flow: clear the session_id from the URL and go to root.
          // MarketingHome / ProtectedRoute will route the user to overview or get-started.
          window.history.replaceState(null, "", "/");
          navigate("/", { replace: true });
          return;
        } catch (err) {
          const status = err?.response?.status;
          console.error(`Auth attempt ${i + 1} failed:`, err?.response?.data || err.message);
          // 403 = denied (no invite). Bail out immediately to /login with a query
          // param so the login page can show the "vraag toegang" banner.
          if (status === 403) {
            const detail = err.response?.data?.detail || "Geen toegang";
            setStatus(detail);
            toast.error(detail);
            setTimeout(() => navigate("/login?denied=1", { replace: true }), 2500);
            return;
          }
          if (i < retries - 1) await new Promise(r => setTimeout(r, 1000));
        }
      }
      setStatus("Google inloggen mislukt. Probeer het opnieuw.");
      toast.error("Google inloggen mislukt. Probeer het opnieuw.");
      setTimeout(() => navigate("/login", { replace: true }), 3000);
    };

    exchangeSession();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm text-slate-500">{status}</p>
      </div>
    </div>
  );
};
